import React from "react";

import { Copy, Pencil, Trash2 } from "lucide-react";

import DataTableColumnHeader from "../../../dataTable/DataTableColumnHeader";
import {
  actionsColumn,
  dateTimeColumn,
} from "../../../dataTable/dataTableColumns";
import DataTableLinkCell from "../../../dataTable/DataTableLinkCell";
import { fastPlaylistEditPath } from "../../../routes/routes";

import type { TFunction } from "i18next";

import type { Playlist } from "../../../../api/FastAPI.types";
import type { DataTableColumnDef } from "../../../dataTable/dataTable.types";

interface FastPlaylistColumnsOptions {
  t: TFunction;
  channelId: string;
  onDuplicate: (playlist: Playlist) => void;
  onEdit: (playlist: Playlist) => void;
  onDelete: (playlist: Playlist) => void;
}

export function getFastPlaylistHref(
  channelId: string,
  playlist: Playlist,
): string {
  return fastPlaylistEditPath(channelId, playlist.uuid);
}

export function fastPlaylistColumns({
  t,
  channelId,
  onDuplicate,
  onEdit,
  onDelete,
}: FastPlaylistColumnsOptions): DataTableColumnDef<Playlist>[] {
  return [
    {
      accessorKey: "name",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("fastPlaylistName")} />
      ),
      meta: { searchable: true, label: t("fastPlaylistName") },
      cell: ({ row }) => (
        <DataTableLinkCell href={getFastPlaylistHref(channelId, row.original)}>
          {row.original.name}
        </DataTableLinkCell>
      ),
    },
    {
      accessorKey: "total_items",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("media_count")} />
      ),
      meta: {
        label: t("media_count"),
        width: "24%",
        minWidth: 175,
        align: "center",
      },
    },
    dateTimeColumn<Playlist>("updated_at", t("update_date")),
    actionsColumn<Playlist>(
      (playlist) => [
        {
          label: t("duplicate"),
          icon: <Copy />,
          onClick: () => onDuplicate(playlist),
        },
        {
          label: t("update"),
          icon: <Pencil />,
          onClick: () => onEdit(playlist),
        },
        {
          label: t("delete"),
          icon: <Trash2 />,
          variant: "destructive",
          onClick: () => onDelete(playlist),
          confirm: {
            title: t("deleteThisPlaylist"),
            description: t("deletePlaylistConfirmation"),
            confirmLabel: t("delete"),
          },
        },
      ],
      (playlist) => playlist.name,
    ),
  ];
}
